import { useTranslate } from '@refinedev/core';
import type { CrudFilter } from '@refinedev/core';
import { List, ShowButton } from '@refinedev/antd';
import {
  Table,
  Input,
  Form,
  Row,
  Col,
  Flex,
  Tag,
  Typography,
  theme,
} from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import type {
  AvailabilityStatus,
  PublicationPremiereListOut,
  PublisherRefOut,
} from '@/api/generated/schemas';
import { useTableSearch } from '@/hooks/useTableSearch';
import { useTableRowNavigateToShow } from '@/hooks/useTableRowNavigateToShow';
import { useAdminTable } from '@/hooks/useAdminTable';
import { ActiveFilters } from '@/components/common/ActiveFilters';
import { PresencePill } from '@/components/common/PresencePill';
import { ShowIconLink } from '@/components/common/ShowIconLink';
import { PublicationIconLink } from '@/components/user_catalog/publications/PublicationIconLink';
import { orDash } from '@/components/common/show/shared';
import { PublicationListCell } from '@/components/user_catalog/publications/PublicationListCell';
import { PublisherSelectFilter } from '@/components/user_catalog/publishers/PublisherSelectFilter';
import { availabilityStatusColors } from '@/pages/user_catalog/publications/constants';
import { isFieldFiltered } from '@/utils/filters';
import { formatDate, formatThousands } from '@/utils/format';
import { getSortOrder } from '@/utils/sorters';
import { RESOURCE } from './constants';
import { countdownKey, daysUntilPremiere } from './premiereCountdown';

const { Text } = Typography;

export const PublicationPremiereList = () => {
  const t = useTranslate();
  const { token } = theme.useToken();
  const rowNav = useTableRowNavigateToShow();
  const { onSearch, createDebouncedSubmit } = useTableSearch();
  const f = (key: string) => t(`${RESOURCE}.fields.${key}`);

  const { tableProps, searchFormProps, setFilters, filters, sorters } =
    useAdminTable<PublicationPremiereListOut>({
      pagination: { pageSize: 20 },
      resource: RESOURCE,
      syncWithLocation: true,
      onSearch,
      // Nearest premiere first. The backend pins the list to future dates, so
      // `published_at` sorts but is not a filter.
      sorters: {
        initial: [{ field: 'published_at', order: 'asc' }],
      },
    });

  const debouncedSubmit = createDebouncedSubmit(searchFormProps.form);
  const crudFilters = filters as CrudFilter[];

  return (
    <List>
      <Form {...searchFormProps} layout="vertical">
        <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
          <Col xs={24} sm={16} md={12} lg={8}>
            <Form.Item name="q" style={{ marginBottom: 0 }}>
              <Input
                placeholder={t(`${RESOURCE}.search.placeholder`)}
                prefix={<SearchOutlined />}
                allowClear
                onChange={(e) => debouncedSubmit(e.target.value)}
              />
            </Form.Item>
          </Col>
        </Row>
      </Form>

      <ActiveFilters
        filters={crudFilters}
        setFilters={setFilters}
        labelPrefix={RESOURCE}
        baseFields={['id']}
      />

      {/* Same pinned layout as the publications list: id + publication left,
          actions right, the middle scrolls. */}
      <Table
        {...rowNav(RESOURCE, tableProps)}
        rowKey="id"
        scroll={{ x: 'max-content' }}
      >
        <Table.Column
          dataIndex="id"
          title={t('base.fields.id')}
          fixed="left"
          width={80}
          sorter
          sortOrder={getSortOrder(sorters, 'id')}
        />

        <Table.Column
          dataIndex="title"
          title={f('title')}
          fixed="left"
          sorter
          sortOrder={getSortOrder(sorters, 'title')}
          render={(_, record: PublicationPremiereListOut) => (
            // `is_premiere` has its own column here, so the cell skips that tag.
            // The icon opens the publication itself; the row opens the premiere.
            <PublicationIconLink publicationId={record.id}>
              <PublicationListCell record={record} showPremiereTag={false} />
            </PublicationIconLink>
          )}
        />

        <Table.Column
          dataIndex="publisher"
          title={f('publisher_id')}
          filtered={isFieldFiltered(crudFilters, 'publisher_id')}
          filterDropdown={({ confirm, clearFilters }) => (
            <PublisherSelectFilter
              field="publisher_id"
              placeholder={f('publisher_id')}
              filters={crudFilters}
              confirm={confirm}
              clearFilters={clearFilters}
              setFilters={setFilters}
            />
          )}
          render={(value?: PublisherRefOut | null) =>
            orDash(
              value && (
                <ShowIconLink resource="user_catalog.publishers" id={value.id}>
                  {value.name}
                </ShowIconLink>
              )
            )
          }
        />

        <Table.Column
          dataIndex="published_at"
          title={f('published_at')}
          sorter
          sortOrder={getSortOrder(sorters, 'published_at')}
          render={(value?: string | null) => {
            const days = daysUntilPremiere(value);
            return orDash(
              value && (
                <Flex vertical>
                  <span>{formatDate(value)}</span>
                  {days != null && (
                    <Text
                      type="secondary"
                      style={{ fontSize: token.fontSizeSM }}
                    >
                      {t(`${RESOURCE}.countdown.${countdownKey(days)}`, {
                        count: days,
                      })}
                    </Text>
                  )}
                </Flex>
              )
            );
          }}
        />

        <Table.Column
          dataIndex="availability_status"
          title={f('availability_status')}
          render={(value: AvailabilityStatus) => (
            <Tag color={availabilityStatusColors[value]}>
              {t(`user_catalog.publications.availability_status.${value}`)}
            </Tag>
          )}
        />

        <Table.Column
          dataIndex="is_premiere"
          title={f('is_premiere')}
          render={(value: boolean) => (
            <PresencePill
              present={value}
              label={t(`${RESOURCE}.followable.${value ? 'yes' : 'no'}`)}
              tooltip={t(`${RESOURCE}.followable.tooltip`)}
            />
          )}
        />

        <Table.Column
          // Not sortable: the backend exposes only id / title / published_at.
          dataIndex="followers_count"
          title={f('followers_count')}
          align="right"
          render={(value: number) => formatThousands(value)}
        />

        <Table.Column
          title={t('actions.column')}
          dataIndex="actions"
          fixed="right"
          width={100}
          // Read-only resource: the only action is opening the premiere's details
          // (the row click does the same).
          render={(_, record: PublicationPremiereListOut) => (
            <ShowButton
              hideText
              size="small"
              resource={RESOURCE}
              recordItemId={record.id}
            />
          )}
        />
      </Table>
    </List>
  );
};
